import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type SectionLock = {
  projectId: string;
  sessionId: string;
  toastFired: boolean;
  message?: string;
  // Set only for locks raised by another user's import (from a 423 body).
  userName?: string;
  lockType: 'import' | 'revert';
};

export type GlobalLockState = {
  sections: Record<string, SectionLock>;
};

const initialState: GlobalLockState = {
  sections: {},
};

const globalLockSlice = createSlice({
  name: 'globalLock',
  initialState,
  reducers: {
    setSectionLocked: (
      state,
      {
        payload,
      }: PayloadAction<{
        sectionType: string;
        projectId: string;
        sessionId: string;
        message?: string;
        userName?: string;
        lockType: SectionLock['lockType'];
      }>,
    ) => {
      // base-api-core already compares the 423/409 body's userId with the
      // current user, so userName is only present for another user's import.
      const existing = state.sections[payload.sectionType];
      if (existing && existing.projectId === payload.projectId) {
        // A retried import can turn into a revert (or back) while the
        // section stays locked; keep the session so the card isn't reopened.
        existing.lockType = payload.lockType;
        existing.userName = payload.userName;
        return;
      }

      state.sections[payload.sectionType] = {
        projectId: payload.projectId,
        sessionId: payload.sessionId,
        toastFired: false,
        message: payload.message,
        userName: payload.userName,
        lockType: payload.lockType,
      };
    },
    unlockSection: (state, { payload }: PayloadAction<string>) => {
      delete state.sections[payload];
    },
    unlockProject: (state, { payload }: PayloadAction<string>) => {
      state.sections = Object.fromEntries(
        Object.entries(state.sections).filter(
          ([, lock]) => lock.projectId !== payload,
        ),
      );
    },
    unlockAll: state => {
      state.sections = {};
    },
    markToastFired: (state, { payload }: PayloadAction<string>) => {
      if (state.sections[payload]) {
        state.sections[payload].toastFired = true;
      }
    },
  },
});

export const {
  setSectionLocked,
  unlockSection,
  unlockProject,
  unlockAll,
  markToastFired,
} = globalLockSlice.actions;

export default globalLockSlice.reducer;
